import type { ComponentConfig } from '@measured/puck';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { easeStripe } from '../../lib/motion';

export interface HeroPrimaryProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  ctaPrimaryLabel?: string;
  ctaPrimaryHref?: string;
  ctaSecondaryLabel?: string;
  ctaSecondaryHref?: string;
}

export function HeroPrimary({
  eyebrow,
  title,
  subtitle,
  ctaPrimaryLabel,
  ctaPrimaryHref,
  ctaSecondaryLabel,
  ctaSecondaryHref,
}: HeroPrimaryProps) {
  return (
    <section className="relative overflow-hidden bg-paper">
      {/* Background subtil */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="dot-grid-masked absolute inset-0" />
        <div className="spot-glow" />
      </div>

      {/* Hairline border bas */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent"
      />

      <Container className="relative py-20 lg:py-28">
        <div className="max-w-3xl">
          {/* Eyebrow chip */}
          {eyebrow ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: easeStripe }}
              className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur"
            >
              <span className="live-dot" aria-hidden />
              <span>{eyebrow}</span>
            </motion.div>
          ) : null}

          {/* Titre */}
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.06, ease: easeStripe }}
            className="mt-6 text-balance font-display text-[clamp(2.5rem,5.5vw,4rem)] font-bold leading-[1.02] tracking-[-0.035em] text-ink"
          >
            {title}
          </motion.h1>

          {/* Subtitle */}
          {subtitle ? (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.16, ease: easeStripe }}
              className="mt-6 max-w-prose text-[17px] leading-relaxed text-stone-600"
            >
              {subtitle}
            </motion.p>
          ) : null}

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.24, ease: easeStripe }}
            className="mt-8 flex flex-wrap items-center gap-2.5"
          >
            {ctaPrimaryLabel && ctaPrimaryHref ? (
              <Button asChild size="lg" variant="primary" className="group">
                <a href={ctaPrimaryHref}>
                  {ctaPrimaryLabel}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                </a>
              </Button>
            ) : null}
            {ctaSecondaryLabel && ctaSecondaryHref ? (
              <Button asChild size="lg" variant="secondary" className="group">
                <a href={ctaSecondaryHref}>
                  {ctaSecondaryLabel}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
                </a>
              </Button>
            ) : null}
          </motion.div>
        </div>
      </Container>
    </section>
  );
}

export const HeroPrimaryConfig: ComponentConfig<HeroPrimaryProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'textarea' },
    subtitle: { type: 'textarea' },
    ctaPrimaryLabel: { type: 'text' },
    ctaPrimaryHref: { type: 'text' },
    ctaSecondaryLabel: { type: 'text' },
    ctaSecondaryHref: { type: 'text' },
  },
  defaultProps: {
    eyebrow: 'Acteur intégré, Travaux, Courtage, Mandataire CEE',
    title: 'Économisez en consommant moins, en payant moins cher, et en valorisant vos chantiers.',
    subtitle:
      'Travaux & rénovation, comparaison de contrats énergie, mandataire CEE pour artisans RGE — un seul interlocuteur, des chiffres documentés, un modèle économique transparent.',
    ctaPrimaryLabel: 'Diagnostic 60 sec',
    ctaPrimaryHref: '/outils',
    ctaSecondaryLabel: 'Voir les 3 pôles',
    ctaSecondaryHref: '/a-propos',
  },
  render: HeroPrimary,
};
